//ShareMap.tsx =================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as MainMap from "../MainMap";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Util from "../Util/Util";
import * as Util_MUI from "../Util/Util_MUI";

import { Button, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Icon, List, ListItemButton, ListItemIcon, ListItemText, Paper, TextField, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import React from "react";
import ReactDOM from "react-dom/client";

let exportFormat: string = "ESRI Shapefile (.shp)";
let exportFormatValue: string = "_shp.zip";

const exportFormats = [
   "AutoCAD Drawing (.dwg)",
   "AutoCAD Exchange (.dxf)",
   "ESRI File Geodatabase (.gdb)",
   "ESRI Shapefile (.shp)",
   "Google Keyhole Markup (.kml)",
   "Microstation (.dgn)",
   "OGC (.geojson)"
];
const exportFormatValues = [
   "_dwg.zip",
   "_dxf.zip",
   "_gdb.zip",
   "_shp.zip",
   "_kml.zip",
   "_dgn.zip",
   "_json.zip"
];

//Show the dialog.
export function show() {
   Util.logMsg("Showing share map");

   if (!Settings.dialogDivRoot) {
      Settings.setDialogDivRoot(ReactDOM.createRoot(document.getElementById("dialog_div")));
   }
   Settings.dialogDivRoot.render(<Render key={Util.uid()} />);
}

//Render function.
export function Render() {

   //Define state.
   const [visible, setVisible] = React.useState(true); //Set refresh method.

   const linkRef = React.useRef<HTMLInputElement | null>(null);
   const embedRef = React.useRef<HTMLInputElement | null>(null);

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
   }, []);

   //Close dialog.
   function handleClose() {
      setVisible(false);
   }

   function copyLinkToClipboard(event: React.MouseEvent<HTMLButtonElement>) {
      copyToClipboard(event, linkRef);
   }

   function copyEmbedToClipboard(event: React.MouseEvent<HTMLButtonElement>) {
      copyToClipboard(event, embedRef);
   }

   function copyToClipboard(event: React.MouseEvent<HTMLButtonElement>, elemRef: React.MutableRefObject<HTMLInputElement>) {
      elemRef.current.select();
      navigator.clipboard.writeText(elemRef.current.value);
      event.currentTarget.focus();
      SnackBar.show("Copied to clipboard", 2);
   }

   function exportFormatChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      exportFormatValue = event.target.value;
      exportFormat = exportFormats[exportFormatValues.indexOf(exportFormatValue)];
      refresh();
   }

   //Build links.
   let link: string = Settings.webServerURL + "/" + Settings.webSitePath;

   if (Search.activePIN === "") {
      const me: __esri.Extent = MainMap.activeView.extent;
      link += "?MapExtent=" + Math.round(parseFloat(me.xmin.toString())) + "," + Math.round(parseFloat(me.ymin.toString())) + "," +
         Math.round(parseFloat(me.xmax.toString())) + "," + Math.round(parseFloat(me.ymax.toString())) + "," + me.spatialReference.wkid;

   } else {
      link += "?pin=" + Search.activePIN.replace(" ", "%20");
   }

   link = link.replace(/ /g, "+");
   const embedLink: string = '<a href="' + link + '" target="_blank" aria-label="GIS Website">GIS Website</a>';

   //css styles.
   const styles: Default.iStyles = {
      row1Container: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', padding: 5, backgroundColor: Settings.leftPanelLabelColor },
      col1Container: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', marginRight: 20 },
      input: { width: 300, fontSize: 14, marginTop: 5, border: 0, backgroundColor: Settings.leftPanelLabelColor },
   };

   const jsx = [];
   let title: string;
   let layName: string;
   let url: string;

   for (const layerName of Settings.download_layerNames) {
      layName = layerName.replace("_", " ").replace("_", " ").replace("_", " ");
      title = "Download " + layName + " in " + exportFormat + " format";
      url = Settings.webServerURL + "/" + Settings.download_folder_path + "/" + layerName + exportFormatValue;

      jsx.push(
         <ListItemButton key={Util.uid()} href={url} disableGutters={true} title={title} aria-label={title}>
            &nbsp;&nbsp;&nbsp;
            <ListItemIcon><Icon>download</Icon></ListItemIcon>
            <ListItemText primary={layName} />
         </ListItemButton>,
      );
   }

   return (
      <ThemeProvider theme={Default.theme}>
         <CssBaseline />
         <Dialog
            role="dialog"
            aria-modal="true"
            id="share-dialog"
            open={visible}
            onClose={handleClose}
            aria-label="Share Map"
            aria-labelledby="share-map-title"
            aria-describedby="share-map-description"
         >
            <DialogTitle id="share-map-title" style={{ paddingBottom: 0, paddingTop: 9 }}>
               Share Map
            </DialogTitle>

            <DialogContent id="share-map-description">
               <Paper style={styles.row1Container}>
                  <div style={styles.col1Container}>
                     <div>
                        <Typography variant="body1">To share this map, copy and paste the link</Typography>
                     </div>

                     <div>
                        <input id="share-link" style={styles.input} type="text" ref={linkRef} value={link} readOnly aria-label="Share link" />
                     </div>
                  </div>

                  <div>
                     <Button variant="outlined" color="info" size="medium" onClick={copyLinkToClipboard} title="Copy link to clipboard" aria-label="Copy link">
                        Copy Link
                     </Button>
                  </div>
               </Paper>

               <br />

               <Paper style={styles.row1Container}>
                  <div style={styles.col1Container}>
                     <div>
                        <Typography variant="body1">To embed in a website</Typography>
                     </div>

                     <div>
                        <input id="embed-link" style={styles.input} type="text" ref={embedRef} value={embedLink} readOnly aria-label="Embed link" />
                     </div>
                  </div>

                  <div>
                     <Button variant="outlined" color="info" size="medium" onClick={copyEmbedToClipboard} title="Copy link to clipboard" aria-label="Copy link">
                        Copy Link
                     </Button>
                  </div>
               </Paper>

               {Settings.download_layerNames.length > 0 && <div>
                  <br />

                  <div className="app-flex-row-start">
                     <Typography variant="h6">Export Map</Typography>

                     <TextField select style={{ marginLeft: 20, width: 230 }}
                        label="Export Format"
                        title="Export Format"
                        slotProps={{
                           htmlInput: { id: "export-format" },
                           inputLabel: { htmlFor: "export-format" }
                        }}
                        value={exportFormatValue}
                        onChange={exportFormatChanged}
                     >
                        {Util_MUI.buildSelectMenuItems(exportFormats, exportFormatValues)}
                     </TextField>
                  </div>

                  <br />

                  <Paper style={styles.row1Container}>
                     <List component="nav" aria-label="Map Layers to download" disablePadding={true} style={{ width: 450 }}>
                        {jsx}
                     </List>
                  </Paper>
               </div>}

            </DialogContent>

            <DialogActions>
               <Button onClick={handleClose} color="info">
                  Close
               </Button>
            </DialogActions>
         </Dialog>
      </ThemeProvider>
   ); //end return.

} //end function.
